import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, Clock, X, UserCheck, Users, HelpCircle, MessageSquare } from "lucide-react";
import { AdminNotification, NotificationType } from "@/hooks/useAdminNotifications";

interface AdminNotificationDialogProps {
  notification: AdminNotification | null;
  onDismiss: () => void;
  onSnooze: () => void;
}

function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case "profile":
      return <UserCheck className="h-6 w-6 text-primary" />;
    case "connection":
      return <Users className="h-6 w-6 text-primary" />;
    case "help_request":
      return <HelpCircle className="h-6 w-6 text-primary" />;
    case "ask_nedra":
      return <MessageSquare className="h-6 w-6 text-primary" />;
    default:
      return <Bell className="h-6 w-6 text-primary" />;
  }
}

function getAdminTabForType(type: NotificationType): string {
  switch (type) {
    case "profile":
      return "profiles";
    case "connection":
      return "connections";
    case "help_request":
      return "connections"; // Help requests are shown under connections tab
    case "ask_nedra":
      return "ask-nedra";
    default:
      return "profiles";
  }
}

export function AdminNotificationDialog({
  notification,
  onDismiss,
  onSnooze,
}: AdminNotificationDialogProps) {
  const navigate = useNavigate();

  if (!notification) return null;

  const handleViewNow = () => {
    const tab = getAdminTabForType(notification.type);
    navigate(`/admin?tab=${tab}`);
    onDismiss();
  };

  return (
    <Dialog open={!!notification} onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              {getNotificationIcon(notification.type)}
            </div>
            <div>
              <DialogTitle className="text-lg">{notification.title}</DialogTitle>
              <DialogDescription className="mt-1">
                {notification.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-2">
          <p className="text-sm text-muted-foreground">
            This item requires your review and approval.
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={onDismiss}
            className="w-full sm:w-auto"
          >
            <X className="mr-2 h-4 w-4" />
            Close
          </Button>
          <Button
            variant="outline"
            onClick={onSnooze}
            className="w-full sm:w-auto"
          >
            <Clock className="mr-2 h-4 w-4" />
            Snooze 5 min
          </Button>
          <Button
            onClick={handleViewNow}
            className="w-full sm:w-auto"
          >
            View Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
